"use strict";
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.CreatePaymentV2 = void 0;
const homi_shared_1 = require("@getgreenline/homi-shared");
const v2_1 = require("../tax/v2");
const decimal_js_1 = require("decimal.js");
const mobx_1 = require("mobx");
class CreatePaymentV2 extends homi_shared_1.CreatePayment {
    constructor(params) {
        const { companyId, taxConfigList, locationId, shiftId, status, pennyRounding } = params;
        super(companyId, locationId, shiftId, status, pennyRounding);
        this.locationId = null;
        this.taxConfigMap = new mobx_1.ObservableMap();
        this.taxRateService = new v2_1.TaxRateService();
        this.setLocationId(locationId);
        this.setTaxConfigMap(taxConfigList !== null && taxConfigList !== void 0 ? taxConfigList : []);
        this.attachReactors();
    }
    setLocationId(locationId) {
        this.locationId = locationId !== null && locationId !== void 0 ? locationId : null;
        this.setAppliedTaxRateRes();
    }
    setTaxConfigMap(taxConfigList) {
        this.taxConfigMap.clear();
        taxConfigList.forEach((taxConfig) => this.taxConfigMap.set(taxConfig.id, taxConfig));
        this.setAppliedTaxRateRes();
    }
    setAppliedTaxRateRes() {
        if (!this.locationId)
            return;
        const taxRateContext = this.buildTaxRateContext(this.locationId);
        this.appliedTaxRateRes = this.taxRateService.processTaxRatesInCents(taxRateContext, this.taxConfigs);
    }
    buildTaxRateContext(locationId) {
        var _a;
        const productPaymentLines = this.paymentLines.filter((paymentLine) => paymentLine.productId);
        const customerIdType = ((_a = this.customer) === null || _a === void 0 ? void 0 : _a.idType) || null;
        return new v2_1.TaxRateContext({
            companyId: this.companyId.toString(),
            locationId: locationId.toString(),
            customerContext: {
                idType: customerIdType,
                consumerType: null,
            },
            itemContextList: productPaymentLines.map((paymentLine) => {
                var _a, _b, _c;
                const taxablePrice = this.calculateTaxablePrice(paymentLine);
                return {
                    productName: `${(_a = paymentLine.product) === null || _a === void 0 ? void 0 : _a.name}`,
                    productId: `${paymentLine.productId}`, // the PRODUCT payment lines always have productId
                    categoryId: paymentLine.categoryId,
                    parentCategoryId: paymentLine.parentCategoryId,
                    productType: (_c = (_b = paymentLine.product) === null || _b === void 0 ? void 0 : _b.productType) !== null && _c !== void 0 ? _c : null,
                    netSubTotal: new decimal_js_1.Decimal(taxablePrice),
                    quantity: new decimal_js_1.Decimal(paymentLine.inventorySubtracted),
                    netDepositFee: new decimal_js_1.Decimal(paymentLine.depositFee), // paymentLine.depositFee is unit deposit fee * quantity
                    cartItemId: null,
                    cannabisType: null,
                    productPotency: null,
                    batchPotency: null,
                };
            }),
        }, CreatePaymentV2.DOLLAR_BASED);
    }
    calculateTaxablePrice(paymentLine) {
        const allDiscountedPrice = paymentLine.productLineDiscountedPrice - Math.abs(paymentLine.cartDiscountPrice);
        return allDiscountedPrice;
    }
    get taxConfigs() {
        return Array.from(this.taxConfigMap.values()).reduce((acc, config) => {
            const shouldBeUsed = this.canUseTaxConfig(config);
            if (!shouldBeUsed)
                return acc;
            const newConfig = new v2_1.TaxConfig({
                id: config.id,
                companyId: config.companyId,
                name: config.name,
                displayName: config.displayName,
                isActive: config.isActive,
                rateType: config.rateType,
                rateAmount: new decimal_js_1.Decimal(config.rateAmount),
                taxType: config.taxType,
                categoryRates: [new v2_1.CategoryRate({ categoryIds: config.categoryIds, rateAmount: null })],
                locationIds: config.locationIds,
                compoundOnTaxConfigIds: config.compoundingTaxes,
                exemptIdentityTypes: config.exemptIdentityTypes,
                taxableFees: config.taxableFees,
                preTax: false,
                cannabisTypeRates: [],
                consumerTypeRates: [],
                useProductPotency: false,
                requirePotency: false,
            }, CreatePaymentV2.DOLLAR_BASED);
            return [...acc, newConfig];
        }, []);
    }
    canUseTaxConfig(config) {
        return config.companyId === this.companyId;
    }
    attachReactors() {
        this.reactToPaymentLineFields();
        this.reactToPaymentLines();
        this.reactToCustomer();
        this.reactToCartDiscount();
    }
    // react to specific field changes within each payment line
    reactToPaymentLineFields() {
        this.setPaymentLineChangeCallback((change) => {
            const shouldResetTaxRateRes = CreatePaymentV2.TAX_V2_LINE_DEPENDENCIES.includes(change.name.toString());
            if (shouldResetTaxRateRes)
                this.setAppliedTaxRateRes();
        });
    }
    // react to addition / removal of payment lines
    reactToPaymentLines() {
        (0, mobx_1.reaction)(() => this.paymentLines, () => {
            this.setAppliedTaxRateRes();
        });
    }
    reactToCustomer() {
        (0, mobx_1.reaction)(() => { var _a; return (_a = this.customer) === null || _a === void 0 ? void 0 : _a.idType; }, () => {
            this.setAppliedTaxRateRes();
        });
    }
    reactToCartDiscount() {
        (0, mobx_1.reaction)(() => [...this.bundleCartDiscountLines, ...this.nonBundleCartDiscountLines], () => {
            this.setAppliedTaxRateRes();
        });
    }
    buildTaxPaymentLines() {
        var _a, _b;
        const taxItems = (_b = (_a = this.appliedTaxRateRes) === null || _a === void 0 ? void 0 : _a.items) !== null && _b !== void 0 ? _b : [];
        const mappedTaxRateItems = new Map(taxItems.map((item) => [item.product.productId, item]));
        return this.paymentLines.reduce((acc, paymentLine, index) => {
            var _a;
            const { productId } = paymentLine;
            const taxItem = productId ? mappedTaxRateItems.get(productId) : undefined;
            const itemTaxDetails = (_a = taxItem === null || taxItem === void 0 ? void 0 : taxItem.taxDetails) !== null && _a !== void 0 ? _a : [];
            if (itemTaxDetails.length === 0) {
                const taxPaymentLineV1 = this.shouldFallbackOnV1Tax(taxItem)
                    ? this.buildTaxPaymentLinesPerPaymentLine(paymentLine, index)
                    : [];
                return [...acc, ...taxPaymentLineV1];
            }
            // otherwise, return v2
            const taxPaymentLinesV2 = itemTaxDetails.map((taxDetail) => ({
                taxId: taxDetail.id,
                amount: taxDetail.appliedTaxAmount,
                displayName: taxDetail.taxName,
                productId: paymentLine.productId,
                paymentLineIndex: index,
            }));
            return [...acc, ...taxPaymentLinesV2];
        }, []);
    }
    shouldFallbackOnV1Tax(taxItem) {
        if (taxItem === null || taxItem === void 0 ? void 0 : taxItem.product.isTaxExemptableProduct)
            return false;
        if (this.customer)
            return true;
        return true;
    }
}
exports.CreatePaymentV2 = CreatePaymentV2;
CreatePaymentV2.TAX_V2_LINE_DEPENDENCIES = [
    'price',
    'discountLoyalty',
    'discountFixed',
    'discountPercentage',
    'inventorySubtracted',
];
CreatePaymentV2.DOLLAR_BASED = false; // money is in cents
__decorate([
    mobx_1.observable
], CreatePaymentV2.prototype, "locationId", void 0);
__decorate([
    mobx_1.observable
], CreatePaymentV2.prototype, "appliedTaxRateRes", void 0);
__decorate([
    mobx_1.action
], CreatePaymentV2.prototype, "setLocationId", null);
__decorate([
    mobx_1.action
], CreatePaymentV2.prototype, "setTaxConfigMap", null);
__decorate([
    mobx_1.action
], CreatePaymentV2.prototype, "setAppliedTaxRateRes", null);
__decorate([
    mobx_1.computed
], CreatePaymentV2.prototype, "taxConfigs", null);
//# sourceMappingURL=CreatePaymentV2.js.map